import { Bar, BarChart, ResponsiveContainer } from "recharts";
import { ArrowDown, ArrowUp, Mail, MousePointer, Eye, MessageSquare } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatNumber } from "@/lib/formatters";
import type { OutreachStat } from "@/api/analytics";

interface OutreachStatsProps {
  stats?: OutreachStat[];
  loading?: boolean;
}

const ICON_FOR: Record<string, LucideIcon> = {
  sent: Mail,
  open_rate: Eye,
  click_rate: MousePointer,
  reply_rate: MessageSquare,
};

/** 4-up outreach card strip — same overall shape as the KPI strip
 *  but with a mini bar chart instead of an area sparkline. The bar
 *  shape signals "discrete sends" whereas the KPI sparkline implies a
 *  smooth trend. */
export function OutreachStats({ stats, loading }: OutreachStatsProps) {
  if (loading) {
    return (
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="h-[110px] animate-pulse rounded-plane bg-white shadow-plane"
          />
        ))}
      </div>
    );
  }
  const safe = stats ?? [];
  if (safe.length === 0) return null;
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      {safe.map((s, idx) => {
        const Icon = ICON_FOR[s.key] ?? Mail;
        return (
          <div
            key={s.key ?? `outreach-${idx}`}
            className="rounded-plane bg-white p-4 shadow-plane"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-slate-100">
                  <Icon className="h-3.5 w-3.5 text-slate-600" />
                </span>
                <span className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
                  {s.label}
                </span>
              </div>
            </div>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="text-[24px] font-semibold tabular-nums tracking-tight text-slate-900">
                {s.unit === "%"
                  ? `${(Math.round(s.value * 10) / 10).toFixed(1)}%`
                  : formatNumber(s.value)}
              </span>
              {s.delta_pct != null && Number.isFinite(s.delta_pct) && (
                <DeltaInline delta={s.delta_pct} />
              )}
            </div>
            {s.sparkline && s.sparkline.length > 1 && (
              <div className="mt-2 h-8">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={s.sparkline}
                    margin={{ top: 1, right: 0, bottom: 1, left: 0 }}
                  >
                    <Bar
                      dataKey="value"
                      fill="#6366f1"
                      radius={[2, 2, 0, 0]}
                      isAnimationActive={false}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function DeltaInline({ delta }: { delta: number }) {
  const rounded = Math.round(delta * 10) / 10;
  const positive = rounded > 0;
  const negative = rounded < 0;
  const Icon = positive ? ArrowUp : negative ? ArrowDown : null;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-0.5 text-[11px] font-medium tabular-nums",
        positive && "text-emerald-600",
        negative && "text-rose-600",
        !positive && !negative && "text-slate-400",
      )}
    >
      {Icon && <Icon className="h-3 w-3" />}
      {Math.abs(rounded)}% vs prev
    </span>
  );
}
